// UserRolesTab.js - User roles management tab
import {
  useState,
  useEffect,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  Chip,
  Switch,
  Alert,
  DataGrid,
  IconButton,
  EditIcon,
  DeleteIcon,
  SettingsIcon,
  PersonAddIcon,
  ExtensionIcon,
  authFetch,
  authFetchRequest,
  useAvailablePages
} from './SettingsCommon';

import RoleDialog from './RoleDialog';
import RolePagesDialog from './RolePagesDialog';
import RoleFeaturesDialog from './RoleFeaturesDialog';
import UserRoleAssignDialog from './UserRoleAssignDialog';

const UserRolesTab = ({ currentUserRole = 'superadmin' }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  // Fetch available pages from API
  const { pages: availablePages, loading: pagesLoading } = useAvailablePages();

  // Dialog states
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [openPagesDialog, setOpenPagesDialog] = useState(false);
  const [openFeaturesDialog, setOpenFeaturesDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [editRoleData, setEditRoleData] = useState(null);
  const [pagesRoleData, setPagesRoleData] = useState(null);
  const [featuresRoleData, setFeaturesRoleData] = useState(null);

  const isSuperAdmin = currentUserRole === 'superadmin';

  // Fetch roles from API
  const fetchRoles = () => {
    setLoading(true);
    setError(null);
    authFetch('/user-roles/')
      .then(data => { setRoles(data); setLoading(false); })
      .catch(err => {
        console.error('Error fetching roles:', err);
        setError('Failed to load roles. Please try again.');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // CRUD handlers
  const handleAddRole = () => {
    setEditRoleData(null);
    setOpenRoleDialog(true);
  };

  const handleEditRole = (role) => {
    setEditRoleData(role);
    setOpenRoleDialog(true);
  };

  const handleSaveRole = async (formData) => {
    try {
      setError(null);
      let updatedRole;
      if (editRoleData?.id) {
        // Update existing role - API returns the updated role
        updatedRole = await authFetchRequest(`/user-roles/${editRoleData.id}`, formData, 'PUT');
        // Update only the changed row in place
        setRoles(prevRoles => prevRoles.map(role =>
          role.id === editRoleData.id ? updatedRole : role
        ));
        // Update selectedRole if it was the edited one
        if (selectedRole?.id === editRoleData.id) {
          setSelectedRole(updatedRole);
        }
      } else {
        // Create new role - API returns the new role
        updatedRole = await authFetchRequest('/user-roles/', formData, 'POST');
        // Add new role to the list
        setRoles(prevRoles => [...prevRoles, updatedRole]);
      }
      setOpenRoleDialog(false);
      setEditRoleData(null);
    } catch (err) {
      console.error('Error saving role:', err);
      setError(`Failed to save role: ${err.message}`);
      throw err; // Re-throw so dialog knows there was an error
    }
  };

  const handleDeleteRole = async (role) => {
    if (role.name === 'superadmin') {
      alert('Cannot delete the superadmin role');
      return;
    }
    if (window.confirm(`Are you sure you want to delete the role "${role.label}"?`)) {
      try {
        setError(null);
        await authFetchRequest(`/user-roles/${role.id}`, null, 'DELETE');
        // Remove the deleted role from the list
        setRoles(prevRoles => prevRoles.filter(r => r.id !== role.id));
        if (selectedRole?.id === role.id) {
          setSelectedRole(null);
        }
      } catch (err) {
        console.error('Error deleting role:', err);
        setError(`Failed to delete role: ${err.message}`);
      }
    }
  };

  const handleToggleActive = async (role) => {
    if (role.name === 'superadmin') {
      alert('Cannot disable the superadmin role');
      return;
    }
    try {
      setError(null);
      const updatedRole = await authFetchRequest(`/user-roles/${role.id}`, {
        ...role,
        active: !role.active
      }, 'PUT');
      // Update only the changed row in place
      setRoles(prevRoles => prevRoles.map(r =>
        r.id === role.id ? updatedRole : r
      ));
      // Update selectedRole if it was the toggled one
      if (selectedRole?.id === role.id) {
        setSelectedRole(updatedRole);
      }
    } catch (err) {
      console.error('Error toggling role active status:', err);
      setError(`Failed to update role: ${err.message}`);
    }
  };

  // Page assignment handlers
  const handleManagePages = (role) => {
    setPagesRoleData(role);
    setOpenPagesDialog(true);
  };

  // Feature assignment handler
  const handleManageFeatures = (role) => {
    setFeaturesRoleData(role);
    setOpenFeaturesDialog(true);
  };

  const handleSavePages = async (updatedRole) => {
    try {
      setError(null);
      const result = await authFetchRequest(`/user-roles/${updatedRole.id}/pages`, {
        pages: updatedRole.pages
      }, 'PUT');
      // Update the role with new pages in place
      const newPages = result.pages || updatedRole.pages;
      setRoles(prevRoles => prevRoles.map(role =>
        role.id === updatedRole.id ? { ...role, pages: newPages } : role
      ));
      // Update selectedRole if it was the edited one
      if (selectedRole?.id === updatedRole.id) {
        setSelectedRole({ ...selectedRole, pages: newPages });
      }
      setOpenPagesDialog(false);
      setPagesRoleData(null);
    } catch (err) {
      console.error('Error saving pages:', err);
      setError(`Failed to save pages: ${err.message}`);
      throw err; // Re-throw so dialog knows there was an error
    }
  };

  // User assignment handler
  const handleAssignUsers = () => {
    setOpenAssignDialog(true);
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'name', headerName: 'Name', width: 120 },
    { field: 'label', headerName: 'Label', width: 150 },
    { field: 'description', headerName: 'Description', width: 200 },
    {
      field: 'pages',
      headerName: 'Pages',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
          {params.value?.length > 0 ? (
            params.value.length <= 3 ? (
              params.value.map(pageId => {
                const page = availablePages.find(p => p.id === pageId);
                return page ? (
                  <Chip key={pageId} label={page.label} size="small" variant="outlined" />
                ) : null;
              })
            ) : (
              <Chip label={`${params.value.length} pages`} size="small" color="primary" />
            )
          ) : (
            <Typography variant="caption" color="textSecondary">No pages</Typography>
          )}
        </Box>
      )
    },
    {
      field: 'active',
      headerName: 'Active',
      width: 100,
      renderCell: (params) => (
        <Switch
          checked={params.value}
          onChange={() => handleToggleActive(params.row)}
          disabled={params.row.name === 'superadmin'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 185,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleManagePages(params.row); }}
            title="Manage Pages"
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleManageFeatures(params.row); }}
            title="Manage Features"
          >
            <ExtensionIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleEditRole(params.row); }}
            title="Edit Role"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleDeleteRole(params.row); }}
            disabled={params.row.name === 'superadmin'}
            title="Delete Role"
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      )
    }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>User Roles Management</Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={handleAddRole}>
          Add Role
        </Button>
        {isSuperAdmin && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PersonAddIcon />}
            onClick={handleAssignUsers}
          >
            Assign Roles to Users
          </Button>
        )}
        <Button variant="outlined" onClick={fetchRoles} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
        <Typography variant="body2">
          Manage user roles and their page access permissions. Click on the settings icon to assign pages to a role.
          {isSuperAdmin && ' As a superadmin, you can also assign roles to users.'}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          autoHeight
          rowHeight={45}
          rows={roles}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRole(params.row)}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#e3f2fd' }
          }}
          getRowClassName={(params) =>
            params.row.id === selectedRole?.id ? 'Mui-selected' : ''
          }
        />
      )}

      {/* Role Create/Edit Dialog */}
      <RoleDialog
        open={openRoleDialog}
        data={editRoleData}
        onClose={() => { setOpenRoleDialog(false); setEditRoleData(null); }}
        onSave={handleSaveRole}
      />

      {/* Role Pages Assignment Dialog */}
      <RolePagesDialog
        open={openPagesDialog}
        role={pagesRoleData}
        availablePages={availablePages}
        onClose={() => { setOpenPagesDialog(false); setPagesRoleData(null); }}
        onSave={handleSavePages}
      />

      {/* Role Features Assignment Dialog */}
      <RoleFeaturesDialog
        open={openFeaturesDialog}
        role={featuresRoleData}
        onClose={() => { setOpenFeaturesDialog(false); setFeaturesRoleData(null); }}
        onSave={() => {}}
      />

      {/* User Role Assignment Dialog (superadmin only) */}
      {isSuperAdmin && (
        <UserRoleAssignDialog
          open={openAssignDialog}
          roles={roles}
          onClose={() => setOpenAssignDialog(false)}
        />
      )}
    </Box>
  );
};

export default UserRolesTab;
